import Badge from '@/common/components/Badge'
import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { UnitTextField } from '@/common/components/UnitTextField'
import { ContractProductCategoryAppendProduct } from '@/features/publicCompanyLeadsFunnel/components/ContractProductCategoryAppendProduct'
import { ContractProductCategoryProductItem } from '@/features/publicCompanyLeadsFunnel/components/ContractProductCategoryProductItem'
import { useUpdatePublicCompanyLeadContractProduct } from '@/features/publicCompanyLeadsFunnel/hooks/useUpdatePublicCompanyLeadContractProduct'
import { publicCompanyLeadContractProductCategoriesDefaultValues } from '@/features/publicCompanyLeadsFunnel/services/publicCompanyLeadContractProductCategoriesDefaultValues'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { BoxesIcon, FileEditIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import {
  contractProductCategorySchema,
  PublicCompanyLeadsFunnelContractProductCategoryValidationSchema,
} from '../schemas/update.schema'
import { ContractProductCategoryData, PublicCompanyLeadData } from '../types/publicCompanyLead'

interface UpdateContractProductCategoryModalProps {
  lead?: PublicCompanyLeadData
  contractProductCategory: ContractProductCategoryData
  productCategoryIndex: number
}

export const UpdateContractProductCategoryModal = ({
  lead,
  contractProductCategory,
  productCategoryIndex,
}: UpdateContractProductCategoryModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { mutate: createPublicCompanyLeadContract } = useUpdatePublicCompanyLeadContractProduct({
    publicCompanyLead: lead,
    contractProductCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', lead?.id] })

      setIsOpen(false)
    },
    onError: (error) => {
      setMessages(error.response?.data.errors.base || ['Ocorreu um erro ao atualizar'])
    },
  })

  const formMethods = useForm<PublicCompanyLeadsFunnelContractProductCategoryValidationSchema>({
    resolver: zodResolver(contractProductCategorySchema),
  })

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = formMethods

  const {
    fields: contractProducts,
    append: appendContractProduct,
    remove: removeContractProduct,
  } = useFieldArray({
    control,
    name: `contract_product_category_products`,
  })

  const onSubmit: SubmitHandler<PublicCompanyLeadsFunnelContractProductCategoryValidationSchema> = (form) =>
    createPublicCompanyLeadContract(form)

  useEffect(() => {
    if (isOpen) {
      reset(publicCompanyLeadContractProductCategoriesDefaultValues({ lead, contractProductCategory }))
    }
  }, [isOpen])

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={(value) => {
          setIsOpen(value)
        }}
        useCancellableFooter
        dataTestid='confirm_contract_product_category_modal_button'
        onConfirm={handleSubmit(onSubmit)}
        cancelButtonClassName="bg-white-100"
        confirmButtonClassName="bg-primary-blue"
        className="min-w-[640px]"
        title={`Detalhando ${contractProductCategory?.product_category?.display_name}`}
        Trigger={
          <Button
            variant="link"
            className="px-2"
            name={`contract_product_category_edit_${productCategoryIndex}`}
          >
            <FileEditIcon strokeWidth={1} />
          </Button>
        }
        body={
          <FormProvider {...formMethods}>
            <form>
              <Box className="flex flex-col gap-4 max-h-[480px] min-h-[320px] overflow-auto text-white-800">
                <Badge
                  Icon={BoxesIcon}
                  iconProps={{ className: 'size-6', strokeWidth: 1 }}
                  text={contractProductCategory?.product_category?.display_name || ''}
                  className="rounded-sm flex bg-neutral-op-8 w-fit flex-row-reverse !p-0 pe-2 gap-0"
                  overwriteClassName
                  contentClassName="px-3 py-2"
                />

                {!!lead?.contract?.require_product_credit_quota && (
                  <div className="flex gap-2">
                    <Controller
                      control={control}
                      name="global_value_in_currency"
                      render={({ field }) => (
                        <CurrencyTextField
                          dataTestid="contract_product_category_value_in_currency_input"
                          label="Cota total em valor"
                          markAsRequired
                          className="w-1/2"
                          {...field}
                          errorMessage={errors?.global_value_in_currency?.message}
                          disabled={false}
                        />
                      )}
                    />

                    <Controller
                      control={control}
                      name="global_value_in_unit_for_consumption"
                      render={({ field }) => (
                        <UnitTextField
                          dataTestid="contract_product_category_value_in_unit_for_consumption_input"
                          label="Cota total em quantidade"
                          markAsRequired
                          className="w-1/2"
                          decimals={2}
                          {...field}
                          errorMessage={errors?.global_value_in_unit_for_consumption?.message}
                          disabled={false}
                        />
                      )}
                    />
                  </div>
                )}

                <ContractProductCategoryAppendProduct
                  productCategoryId={contractProductCategory?.product_category?.id}
                  contractProducts={contractProducts}
                  appendContractProduct={appendContractProduct}
                />

                {contractProducts?.map((contractProduct, index) => (
                  <ContractProductCategoryProductItem
                    key={contractProduct.product?.id}
                    lead={lead}
                    contractProductIndex={index}
                    contractProduct={contractProduct}
                    removeContractProduct={removeContractProduct}
                  />
                ))}
              </Box>
            </form>
          </FormProvider>
        }
      />

      {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
    </>
  )
}
